import { useState, useCallback } from 'react';
import { classifyHTAStage, calculateFraminghamRisk, checkMetabolicSyndrome } from '../../../../utils/internalMedicineRisk';
import {
  type InternalMedicineData,
  emptyInternalMedicineData,
} from './types';

function getAgeInMonths(birthDate: string): number {
  const birth = new Date(birthDate);
  const now = new Date();
  let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) months--;
  return Math.max(0, months);
}

export const useInternalMedicineForm = (
  patientBirthDate: string | null,
  patientGender: boolean | null,
) => {
  const [internalMedicineData, setInternalMedicineData] = useState<InternalMedicineData>(emptyInternalMedicineData);

  const setInternalMedicineField = useCallback(
    (section: keyof InternalMedicineData, field: string, value: any) => {
      setInternalMedicineData(prev => {
        const updated = {
          ...prev,
          [section]: {
            ...(prev[section] as any),
            [field]: value,
          },
        };

        // Auto-classify HTA stage when BP changes
        if (section === 'hypertension' && (field === 'avg_sbp' || field === 'avg_dbp')) {
          const sbp = updated.hypertension.avg_sbp;
          const dbp = updated.hypertension.avg_dbp;
          if (sbp !== null && dbp !== null && !isNaN(Number(sbp)) && !isNaN(Number(dbp))) {
            const result = classifyHTAStage(Number(sbp), Number(dbp));
            updated.hypertension.calculated_stage = result.stage;
            if (updated.hypertension.stage_overridden) {
              updated.hypertension.stage_overridden = false;
            }
          }
        }

        // Auto-calculate Framingham when lipid panel changes
        if (section === 'dyslipidemia' && ['ldl', 'hdl', 'triglycerides', 'total_cholesterol'].includes(field)) {
          const birthDate = patientBirthDate;
          const gender = patientGender;
          if (birthDate && gender !== null) {
            const age = Math.round(getAgeInMonths(birthDate) / 12);
            const sex: 'male' | 'female' = gender ? 'male' : 'female';
            const tc = updated.dyslipidemia.total_cholesterol;
            const hdl = updated.dyslipidemia.hdl;
            const sbp = updated.hypertension.avg_sbp;

            if (tc !== null && hdl !== null && sbp !== null && age > 0) {
              const smoking = updated.cv_risk.risk_factors.includes('smoking');
              const diabetes = updated.diabetes.type !== '';
              const treated = updated.hypertension.current_treatment.length > 0;

              const result = calculateFraminghamRisk({
                age,
                sex,
                totalCholesterol: tc,
                hdl,
                sbp,
                smoking,
                diabetes,
                treated,
              });

              updated.cv_risk = {
                ...updated.cv_risk,
                calculated_risk_percent: result.risk_percent,
                calculated_risk_category: result.category,
                data_complete: result.data_complete,
                missing_factors: result.missing_factors,
              };
            }
          }
        }

        // Auto-calculate metabolic syndrome when waist changes
        if (section === 'metabolic_syndrome' && field === 'waist_circumference_cm') {
          const gender = patientGender;
          if (gender !== null) {
            const sex: 'male' | 'female' = gender ? 'male' : 'female';
            const result = checkMetabolicSyndrome({
              waist_cm: updated.metabolic_syndrome.waist_circumference_cm,
              triglycerides: updated.dyslipidemia.triglycerides,
              hdl: updated.dyslipidemia.hdl,
              sbp: updated.hypertension.avg_sbp,
              dbp: updated.hypertension.avg_dbp,
              fasting_glucose: updated.diabetes.fasting_glucose,
              sex,
            });

            const findCriterion = (id: string) => result.criteria.find(c => c.id === id)?.positive ?? false;

            updated.metabolic_syndrome = {
              ...updated.metabolic_syndrome,
              waist_high: findCriterion('waist'),
              triglycerides_high: findCriterion('triglycerides'),
              hdl_low: findCriterion('hdl'),
              bp_high: findCriterion('blood_pressure'),
              fasting_glucose_high: findCriterion('fasting_glucose'),
              criteria_positive_count: result.count,
              diagnosis: result.diagnosis,
            };
          }
        }

        return updated;
      });
    },
    [patientBirthDate, patientGender],
  );

  const loadFromConsultation = (rawData: any, birthDate?: string | null, gender?: boolean | null) => {
    const rawInternalMedicine = (rawData as any).internal_medicine;
    if (!rawInternalMedicine) return;

    const parsed: InternalMedicineData = {
      ...emptyInternalMedicineData,
      ...rawInternalMedicine,
      diabetes: { ...emptyInternalMedicineData.diabetes, ...(rawInternalMedicine.diabetes || {}) },
      hypertension: { ...emptyInternalMedicineData.hypertension, ...(rawInternalMedicine.hypertension || {}) },
      dyslipidemia: { ...emptyInternalMedicineData.dyslipidemia, ...(rawInternalMedicine.dyslipidemia || {}) },
      cv_risk: { ...emptyInternalMedicineData.cv_risk, ...(rawInternalMedicine.cv_risk || {}) },
      metabolic_syndrome: { ...emptyInternalMedicineData.metabolic_syndrome, ...(rawInternalMedicine.metabolic_syndrome || {}) },
      treatment_plan: { ...emptyInternalMedicineData.treatment_plan, ...(rawInternalMedicine.treatment_plan || {}) },
    };

    const bd = birthDate ?? patientBirthDate;
    const g = gender ?? patientGender;

    if (parsed.hypertension.avg_sbp !== null && parsed.hypertension.avg_dbp !== null) {
      const htaResult = classifyHTAStage(parsed.hypertension.avg_sbp, parsed.hypertension.avg_dbp);
      parsed.hypertension.calculated_stage = htaResult.stage;
    }

    if (g !== null && bd) {
      const age = Math.round(getAgeInMonths(bd) / 12);
      const tc = parsed.dyslipidemia.total_cholesterol;
      const hdl = parsed.dyslipidemia.hdl;
      const sbp = parsed.hypertension.avg_sbp;

      if (tc !== null && hdl !== null && sbp !== null && age > 0) {
        const sex: 'male' | 'female' = g ? 'male' : 'female';
        const smoking = parsed.cv_risk.risk_factors.includes('smoking');
        const diabetes = parsed.diabetes.type !== '';
        const treated = parsed.hypertension.current_treatment.length > 0;

        const frResult = calculateFraminghamRisk({ age, sex, totalCholesterol: tc, hdl, sbp, smoking, diabetes, treated });
        parsed.cv_risk = {
          ...parsed.cv_risk,
          calculated_risk_percent: frResult.risk_percent,
          calculated_risk_category: frResult.category,
          data_complete: frResult.data_complete,
          missing_factors: frResult.missing_factors,
        };
      }

      const msResult = checkMetabolicSyndrome({
        waist_cm: parsed.metabolic_syndrome.waist_circumference_cm,
        triglycerides: parsed.dyslipidemia.triglycerides,
        hdl: parsed.dyslipidemia.hdl,
        sbp: parsed.hypertension.avg_sbp,
        dbp: parsed.hypertension.avg_dbp,
        fasting_glucose: parsed.diabetes.fasting_glucose,
        sex: g ? 'male' : 'female',
      });

      const findCriterion = (id: string) => msResult.criteria.find(c => c.id === id)?.positive ?? false;
      parsed.metabolic_syndrome = {
        ...parsed.metabolic_syndrome,
        waist_high: findCriterion('waist'),
        triglycerides_high: findCriterion('triglycerides'),
        hdl_low: findCriterion('hdl'),
        bp_high: findCriterion('blood_pressure'),
        fasting_glucose_high: findCriterion('fasting_glucose'),
        criteria_positive_count: msResult.count,
        diagnosis: msResult.diagnosis,
      };
    }

    setInternalMedicineData(parsed);
  };

  const getPayloadData = (): InternalMedicineData | null => {
    const hasData =
      internalMedicineData.diabetes.type !== '' ||
      internalMedicineData.diabetes.hba1c !== null ||
      internalMedicineData.diabetes.fasting_glucose !== null ||
      internalMedicineData.hypertension.stage !== '' ||
      internalMedicineData.hypertension.avg_sbp !== null ||
      internalMedicineData.dyslipidemia.ldl !== null ||
      internalMedicineData.dyslipidemia.hdl !== null ||
      internalMedicineData.dyslipidemia.triglycerides !== null ||
      internalMedicineData.dyslipidemia.total_cholesterol !== null ||
      internalMedicineData.cv_risk.risk_factors.length > 0 ||
      internalMedicineData.metabolic_syndrome.waist_circumference_cm !== null ||
      internalMedicineData.treatment_plan.plan_notes !== '';
    return hasData ? internalMedicineData : null;
  };

  return {
    internalMedicineData,
    setInternalMedicineData,
    setInternalMedicineField,
    loadFromConsultation,
    getPayloadData,
  };
};
